"use client";

import { useEffect, useRef, useState } from "react";

export type NavItem = { id: string };

export type UseActiveItemIndexOptions = {
  /** Pixel offset from viewport top to determine current section. Default: 0 */
  offset?: number;
  /** Read initial active index from URL hash on mount. Default: true */
  initialFromHash?: boolean;
  /** Update active index when the hash changes after mount. Default: true */
  listenToHashChange?: boolean;
};

export default function useActiveItemIndex(
  navItems: NavItem[],
  {
    offset = 0,
    initialFromHash = true,
    listenToHashChange = true,
  }: UseActiveItemIndexOptions = {},
) {
  const [initialActiveItemIndex, setInitialActiveItemIndex] = useState<
    number | undefined
  >(undefined);
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return; // SSR/SSG guard
    }

    const computeCurrentIndex = () => {
      return navItems
        .map((navItem) => document.getElementById(navItem.id))
        .filter((el): el is HTMLElement => Boolean(el))
        .findIndex((section) => {
          const rect = section.getBoundingClientRect();
          if (offset > 0) {
            section.style.scrollMarginTop = `${offset}px`;
          }
          return rect.top <= offset && rect.bottom >= offset + 20;
        });
    };

    const initFromHashOrView = () => {
      if (
        initialFromHash &&
        window.location.hash &&
        initialActiveItemIndex === undefined
      ) {
        const hash = decodeURIComponent(window.location.hash.slice(1));
        const idx = navItems.findIndex((n) => n.id === hash);
        if (idx !== -1) {
          setActiveItemIndex(idx);
          setInitialActiveItemIndex(idx);
        } else {
          // hash is not part of navigation but inside the items =>
          // scroll to it and runCompute to set active section index
          const currentIdx = computeCurrentIndex();
          if (currentIdx !== -1) {
            setActiveItemIndex(currentIdx);
            setInitialActiveItemIndex(currentIdx);
          }
        }
        // if anchor is not in any section but somewhere on the page scroll to it
        const targetElement = document.getElementById(hash);
        if (targetElement) {
          const performScroll = () => {
            targetElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          };

          // Wait for DOM to be fully ready to avoid layout shifts from loading content
          if (document.readyState === "complete") {
            // Already loaded, use requestAnimationFrame + small delay for any async content
            requestAnimationFrame(() => {
              setTimeout(performScroll, 100);
            });
          } else {
            // Wait for all resources (images, etc.) to load
            window.addEventListener(
              "load",
              () => {
                requestAnimationFrame(() => {
                  setTimeout(performScroll, 50);
                });
              },
              { once: true },
            );
          }
        }
        return;
      }
      runCompute();
    };

    const runCompute = () => {
      const idx = computeCurrentIndex();
      if (idx !== -1) {
        // Avoid unnecessary re-renders
        setActiveItemIndex((prev) => (prev === idx ? prev : idx));
      }
    };

    const onScroll = () => {
      if (rafRef.current == null) {
        rafRef.current = window.requestAnimationFrame(() => {
          rafRef.current = null;
          runCompute();
        });
      }
    };

    const onHashChange = () => {
      if (!listenToHashChange) return;
      if (window.location.hash) {
        const hash = decodeURIComponent(window.location.hash.slice(1));
        const idx = navItems.findIndex((n) => n.id === hash);
        if (idx !== -1) setActiveItemIndex(idx);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    if (listenToHashChange) window.addEventListener("hashchange", onHashChange);

    // Initial calculation
    initFromHashOrView();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (listenToHashChange)
        window.removeEventListener("hashchange", onHashChange);
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navItems, offset, initialFromHash, listenToHashChange]);

  return {
    activeItemIndex,
    initialActiveItemIndex:
      typeof window !== "undefined" && !window.location.hash
        ? 0
        : initialActiveItemIndex,
  };
}
